import { Fragment, useMemo, useState } from "react";
import type { Book, FrameRecords, RollforwardFileSummary, RollforwardResponse, SideDisplayConfig } from "../types/api";
import { api } from "../api/client";
import DataTable from "../components/DataTable";
import KpiCard, { fmtAed } from "../components/KpiCard";
import { monthLabel, prettyDate } from "../components/ReconMonthDialog";
import { useToast } from "../components/Toast";
import useElapsed from "../components/useElapsed";
import { useColumnWidths } from "../components/useColumnWidths";

interface Props {
  result: RollforwardResponse;
  insurerName: string;
  book: Book;
  // A sidebar tolerance change re-runs the check in place (UploadPage); the result shown
  // stays the previous one until it lands, or if it fails.
  rerunning: boolean;
  rerunError: string;
  onBack: () => void;
  onContinue: () => void;
}

type Row = FrameRecords["rows"][number];

// Review order, the engine's STATUS_ORDER (backend/customer_rollforward.py).
const NO_VARIANCE = "No variance";
const NEW_CUSTOMER = "New customer";
const STATUS_ORDER = ["Customer missing from current", "New customer, back-dated", "History changed",
                      "Not comparable", "Within tolerance", "Customer missing, zero balance",
                      NEW_CUSTOMER, NO_VARIANCE];
const STATUS_TONE: Record<string, string> = {
  "Customer missing from current": "risk", "New customer, back-dated": "risk",
  "History changed": "risk", "Not comparable": "warn", "Within tolerance": "ok",
  "Customer missing, zero balance": "info", [NEW_CUSTOMER]: "info", [NO_VARIANCE]: "ok",
};
// The engine's STATUS_NO_REVIEW: listed and filterable, never counted as to review.
const NO_REVIEW = [NEW_CUSTOMER, NO_VARIANCE];
// Every customer of the other statuses, the workbook's review statuses.
const REVIEW = "To review";
const PAGE = 100;
const MONEY_HEADER = /amount|debit|credit|balance/i;
// Column keys for the fixed-header tables' resizable widths (useColumnWidths).
const LINES_COLS = ["source", "amount", "customers", "rows"];
const PARTNER_COLS = ["partner", "prev_close", "variance", "movement", "curr_close", "customers",
                      "not_in_current", "found_prev", "found_curr"];
const PLACE_COLS = ["file", "statement", "sheet", "customers"];
const CUSTOMER_COLS = ["x", "customer", "prev_close", "curr_open", "variance", "movement", "curr_close", "status"];
const NOT_COUNTED_COLS = ["Statement", "Reason", "Rows", "Amount"];
const STATEMENT_COLS = ["Doc date", "Voucher", "Narration", "Debit", "Credit", "Balance", "__flag"];

type Tab = "customers" | "explaining" | "excluded" | "checks";
const TABS: [Tab, string][] = [["customers", "Customers"], ["explaining", "Variance transactions"],
                               ["excluded", "Rows not counted"], ["checks", "Checks"]];

// Column accessor over a FrameRecords row: frame.columns names the positions.
function cols(frame: FrameRecords) {
  const at = Object.fromEntries(frame.columns.map((c, i) => [c, i]));
  return (row: Row, c: string) => (at[c] === undefined ? null : row[at[c]]);
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

// "2026-01-23" / "2026-01-23T00:00:00" -> "23 Jan 2026"; blank when undated.
function shortDate(v: unknown): string {
  if (typeof v !== "string" || v.length < 10) return "";
  const [y, m, d] = v.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return v;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

// The frame narrowed to `keep` (the flat list must not sprout the per-side block cells).
function pick(frame: FrameRecords, keep: string[]): FrameRecords {
  const idx = keep.map((c) => frame.columns.indexOf(c)).filter((i) => i >= 0);
  return { ...frame, columns: idx.map((i) => frame.columns[i]),
           rows: frame.rows.map((r) => idx.map((i) => r[i])) };
}

// One raw cell as the statement prints it: dates as dates, money as money.
function cellText(header: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number") return MONEY_HEADER.test(header) ? fmtAed(v) : String(v);
  if (/date|dt$/i.test(header)) return shortDate(v) || String(v);
  return String(v);
}

function groupBy(frame: FrameRecords, key: string): Map<string, Row[]> {
  const get = cols(frame);
  const out = new Map<string, Row[]>();
  for (const r of frame.rows) {
    const k = String(get(r, key) ?? "");
    const list = out.get(k);
    if (list) list.push(r); else out.set(k, [r]);
  }
  return out;
}

function Money({ v, tone }: { v: number; tone?: boolean }) {
  const zero = Math.abs(v) < 0.005;
  return <td className={tone && !zero ? "rf-num rf-flagnum" : "rf-num"}>{fmtAed(zero ? 0 : v)}</td>;
}

// A text cell that truncates with an ellipsis (a td alone ignores max-width).
function Ell({ text, className }: { text: string; className?: string }) {
  return <td className={className}><div className="rf-ell" title={text}>{text}</div></td>;
}

function Pill({ status }: { status: string }) {
  return <span className={`rf-pill rf-pill--${STATUS_TONE[status] ?? "info"}`}>{status}</span>;
}

export default function RollforwardPage({ result, insurerName, book, rerunning, rerunError, onBack, onContinue }: Props) {
  const s = result.summary;
  const cut = prettyDate(s.previous_cut_off);
  const to = prettyDate(s.current_to);
  const [tab, setTab] = useState<Tab>("customers");
  const [filter, setFilter] = useState<string>(REVIEW);
  const [partner, setPartner] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"review" | "grid">("review");
  const [open, setOpen] = useState<string>("");
  const [shown, setShown] = useState(PAGE);
  const [exporting, setExporting] = useState(false);
  const elapsed = useElapsed(exporting);
  const rerunElapsed = useElapsed(rerunning);
  const toast = useToast();

  const counts = s.status_counts;
  const lines = s.by_status.filter((b) => !NO_REVIEW.includes(b.status));
  const clean = s.by_status.find((b) => b.status === NO_VARIANCE);
  const fresh = s.by_status.find((b) => b.status === NEW_CUSTOMER);
  const review = s.customers - (clean?.customers ?? 0) - (fresh?.customers ?? 0);
  const get = useMemo(() => cols(result.customers), [result.customers]);
  const explainBy = useMemo(() => groupBy(result.explaining, "account"), [result.explaining]);
  const statementBy = useMemo(() => groupBy(result.transactions, "account"), [result.transactions]);
  const explainFlat = useMemo(() => pick(result.explaining,
    ["account", "name", "change", "doc_date", "voucher", "narration", "amount", "file",
     "counterpart_account", "note"]), [result.explaining]);

  // Customers in review order (status priority, largest variance first), filtered.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kept = result.customers.rows.filter((r) => {
      const st = String(get(r, "status"));
      if (filter === REVIEW ? NO_REVIEW.includes(st) : filter && st !== filter) return false;
      if (partner && get(r, "partner") !== partner) return false;
      return !q || `${get(r, "account")} ${get(r, "name")}`.toLowerCase().includes(q);
    });
    return kept.sort((a, b) => {
      const ra = STATUS_ORDER.indexOf(String(get(a, "status")));
      const rb = STATUS_ORDER.indexOf(String(get(b, "status")));
      return ra !== rb ? ra - rb : Math.abs(num(get(b, "variance"))) - Math.abs(num(get(a, "variance")));
    });
  }, [result.customers, get, filter, partner, query]);
  // The tab-only columns stay out: "Previous/Current file / tab" already name the tab.
  const gridFrame: FrameRecords = useMemo(() => {
    const keep = result.customers.columns.map((c, i) => [c, i] as const)
      .filter(([c]) => c !== "previous_tabs" && c !== "current_tabs");
    return {
      columns: keep.map(([c]) => c), rows: rows.map((r) => keep.map(([, i]) => r[i])),
      indices: rows.map((_, i) => i), total_rows: rows.length,
    };
  }, [result.customers.columns, rows]);
  const gridGet = useMemo(() => cols(gridFrame), [gridFrame]);
  const totals = useMemo(() => ["prev_close", "curr_open", "variance", "movement", "curr_close"]
    .map((c) => rows.reduce((t, r) => t + num(get(r, c)), 0)), [rows, get]);

  const partners = s.by_partner ?? [];
  const hasPartner = result.customers.columns.includes("partner");
  const customerConfig: SideDisplayConfig = useMemo(() => ({
    columns: ["account", "name", ...(hasPartner ? ["partner"] : []), "status", "prev_close",
              "curr_open", "variance",
              ...result.movement_buckets.map((b) => `mv_${b}`), "movement", "curr_close",
              "cause", "reason", "previous_source", "current_source", "basis"],
    renames: {
      account: "A/C No", name: "Customer", partner: "Partner", status: "Status",
      prev_close: `Balance ${cut} previous`, curr_open: `Balance ${cut} current`,
      variance: "Variance", movement: "Total movement", curr_close: `Closing ${to}`,
      cause: "Cause", reason: "Reason", previous_source: "Previous file / tab",
      current_source: "Current file / tab", basis: "Basis",
      ...Object.fromEntries(result.movement_buckets.map((b) => [`mv_${b}`, b])),
    },
  }), [result.movement_buckets, cut, to, hasPartner]);
  const explainConfig: SideDisplayConfig = {
    columns: ["account", "name", "change", "doc_date", "voucher", "narration", "amount",
              "file", "counterpart_account", "note"],
    renames: { account: "A/C No", name: "Customer", change: "What changed", doc_date: "Doc Date",
               voucher: "Voucher", narration: "Narration", amount: "Effect on variance",
               file: "Found in", counterpart_account: "Other A/C", note: "Note" },
  };
  const excludedConfig: SideDisplayConfig = {
    columns: ["file", "reason", "account", "name", "date", "voucher", "narration", "amount", "sheet"],
    renames: { file: "Statement", reason: "Reason", account: "A/C No", name: "Customer",
               date: "Doc Date", voucher: "Voucher", narration: "Narration", amount: "Amount",
               sheet: "Tab" },
  };

  async function download() {
    setExporting(true);
    try {
      const blob = await api.exportCustomerRollforward(result.rollforward_id, book);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Customer Roll-forward - ${insurerName} - ${s.previous_cut_off} to ${s.current_to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setExporting(false);
    }
  }

  const untied = (["Previous", "Current"] as const).filter(
    (k) => Math.abs(s.files[k].tie_out.difference) >= 0.01
      || Math.abs(s.files[k].own_total_difference) >= 0.01);
  const pickFilter = (k: string) => { setFilter(k); setShown(PAGE); setOpen(""); setTab("customers"); };
  // A partner line lists all its customers; clicking it again clears the partner filter.
  const pickPartner = (p: string) => {
    setPartner(p === partner ? "" : p); setFilter(""); setShown(PAGE); setOpen(""); setTab("customers");
  };
  // Status filter above the table, the same filters the overview lines set.
  const chips = [REVIEW, "", ...STATUS_ORDER.filter((k) => counts[k])];
  const chipCount = (k: string) => (k === REVIEW ? review : k === "" ? s.customers : counts[k] ?? 0);
  const linesCw = useColumnWidths(LINES_COLS, result);
  const partnerCw = useColumnWidths(PARTNER_COLS, result);
  const placeCw = useColumnWidths(PLACE_COLS, result);
  const custCw = useColumnWidths(CUSTOMER_COLS, result);
  const moneyTexts = (c: string) => () => rows.map((r) => {
    const v = num(get(r, c));
    return fmtAed(Math.abs(v) < 0.005 ? 0 : v);
  });
  const notCountedCw = useColumnWidths(NOT_COUNTED_COLS, result);

  return (
    <div className="page-body rf-results">
      {/* The main results page's header, buttons and progress feedback, so step 1 reads
          as part of the same app (ResultPage.tsx). */}
      <div>
        <div className="page-head">
          <div className="page-head-left">
            <h2>CRI x {insurerName} — Customer Variance Check</h2>
            <span className="period-pill">
              {monthLabel(s.previous_cut_off.slice(0, 7))} vs {monthLabel(s.current_to.slice(0, 7))} · compared at {cut}
            </span>
            <button type="button" className={`badge-btn ${untied.length ? "badge-unreconciled" : "badge-reconciled"}`}
              onClick={() => setTab("checks")}>
              {untied.length ? `${untied.join(" AND ").toUpperCase()} STATEMENT: CHECK` : "CHECKS PASSED"}
            </button>
          </div>
          <div className="btn-row">
            <button className="btn" onClick={onBack}>Edit inputs</button>
            {/* Mid re-run the stored check is the old tolerance's: no workbook from it. */}
            <button className="btn" onClick={download} disabled={exporting || rerunning}>
              {exporting ? "Preparing…" : "Download Workbook"}
            </button>
            <button className="btn btn-primary" onClick={onContinue}>Continue to reconciliation →</button>
          </div>
        </div>
        {exporting && (
          <>
            <div className="progress-indeterminate" style={{ marginTop: 10, maxWidth: 420 }} />
            <p className="muted" style={{ margin: "6px 0 0" }}>
              Building the workbook — the download starts automatically when it's ready; you
              don't need to click again.{elapsed > 0 && ` · ${elapsed}s elapsed`}
            </p>
          </>
        )}
        {rerunError && !rerunning && (
          <div className="badge badge-risk" role="alert" style={{ display: "block", marginTop: 10 }}>
            Could not re-run the check: {rerunError}
          </div>
        )}
        <hr className="rule" />
      </div>

      <div className="row">
        <KpiCard label={`Balance at ${cut} per previous statement`} value={fmtAed(s.totals.prev_close)} />
        <KpiCard label={`Balance at ${cut} per current statement`} value={fmtAed(s.totals.curr_open)} />
        <KpiCard label="Variance (current − previous)" value={fmtAed(s.totals.variance)} accent="primary"
          note={`${review.toLocaleString()} of ${s.customers.toLocaleString()} customers to review`} />
      </div>
      {/* The tolerance these figures were computed WITH (engine summary), not the sidebar
          box, which runs ahead of the result mid re-run or after a failed one. */}
      <p className="muted text-center" style={{ marginTop: 12 }}>
        Matched tolerance in effect — Premium: AED {s.tolerance.toFixed(2)}. A customer whose
        variance is within it reads "Within tolerance". Adjust in the sidebar; the check
        re-runs when you leave the field or press Enter.
      </p>

      {untied.length > 0 && (
        <p className="rf-banner" role="status">
          The {untied.join(" and ").toLowerCase()} statement does not tie to its own customer
          total. <button type="button" className="rf-link" onClick={() => setTab("checks")}>See Checks</button>
        </p>
      )}

      <div className="rf-table-wrap">
        <table className={"rf-table rf-lines" + linesCw.className} style={linesCw.style}>
          {linesCw.colgroup}
          <thead>
            <tr>
              <th>Where the variance comes from<small>click a line to list its customers</small>{linesCw.grip("source")}</th>
              <th className="rf-num">Amount (AED){linesCw.grip("amount")}</th>
              <th className="rf-num">Customers{linesCw.grip("customers")}</th>
              <th className="rf-num">Rows{linesCw.grip("rows")}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((b) => (
              <Fragment key={b.status}>
                <tr className={`rf-row${filter === b.status ? " rf-row--open" : ""}`} onClick={() => pickFilter(b.status)}>
                  <td><Pill status={b.status} /></td>
                  <Money v={b.variance} />
                  <td className="rf-num">{b.customers.toLocaleString()}</td>
                  <td className="rf-num">{b.rows.toLocaleString()}</td>
                </tr>
                {b.changes.map((k) => (
                  <tr key={k.change} className="rf-line-sub">
                    <td>{k.change}</td><Money v={k.amount} />
                    <td className="rf-num">{k.customers.toLocaleString()}</td>
                    <td className="rf-num">{k.rows.toLocaleString()}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className={`rf-row${filter === REVIEW ? " rf-row--open" : ""}`} onClick={() => pickFilter(REVIEW)}>
              <td>Total variance</td><Money v={s.totals.variance} />
              <td className="rf-num">{review.toLocaleString()}</td>
              <td className="rf-num">{(result.explaining.total_rows - (clean?.rows ?? 0)).toLocaleString()}</td>
            </tr>
            <tr className={`rf-row rf-line-clean${filter === NO_VARIANCE ? " rf-row--open" : ""}`}
              onClick={() => pickFilter(NO_VARIANCE)}>
              <td>Customers that agree (no variance)</td><Money v={0} />
              <td className="rf-num">{(clean?.customers ?? 0).toLocaleString()}</td>
              <td className="rf-num">{clean?.rows ? clean.rows.toLocaleString() : ""}</td>
            </tr>
            {fresh && (
              <tr className={`rf-row rf-line-clean${filter === NEW_CUSTOMER ? " rf-row--open" : ""}`}
                onClick={() => pickFilter(NEW_CUSTOMER)}>
                <td>New customers (first entry after {cut})</td><Money v={0} />
                <td className="rf-num">{fresh.customers.toLocaleString()}</td>
                <td className="rf-num" />
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {partners.length > 0 && (
        <div className="rf-table-wrap">
          <table className={"rf-table" + partnerCw.className} style={partnerCw.style}>
            {partnerCw.colgroup}
            <thead>
              <tr>
                <th>Balance by partner<small>click a line to list its customers</small>{partnerCw.grip("partner")}</th>
                <th className="rf-num">Balance {shortDate(s.previous_cut_off)}<small>previous statement</small>{partnerCw.grip("prev_close")}</th>
                <th className="rf-num">Variance{partnerCw.grip("variance")}</th>
                <th className="rf-num">Movement<small>to {shortDate(s.current_to)}</small>{partnerCw.grip("movement")}</th>
                <th className="rf-num">Closing {shortDate(s.current_to)}{partnerCw.grip("curr_close")}</th>
                <th className="rf-num">Customers{partnerCw.grip("customers")}</th>
                <th className="rf-num">Not in current{partnerCw.grip("not_in_current")}</th>
                <th>Found in<small>previous statement</small>{partnerCw.grip("found_prev")}</th>
                <th>Found in<small>current statement</small>{partnerCw.grip("found_curr")}</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.partner} className={`rf-row${partner === p.partner ? " rf-row--open" : ""}`}
                  onClick={() => pickPartner(p.partner)}>
                  <Ell text={p.partner} />
                  <Money v={p.prev_close} /><Money v={p.variance} /><Money v={p.movement} /><Money v={p.curr_close} />
                  <td className="rf-num">{p.customers.toLocaleString()}</td>
                  <td className="rf-num">{p.not_in_current ? p.not_in_current.toLocaleString() : ""}</td>
                  <Ell text={p.found_in.Previous || "not in the previous statement"} />
                  <Ell text={p.found_in.Current || "not in the current statement"} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <Money v={s.totals.prev_close} /><Money v={s.totals.variance} />
                <Money v={s.totals.movement} /><Money v={s.totals.curr_close} />
                <td className="rf-num">{s.customers.toLocaleString()}</td>
                <td className="rf-num">{partners.reduce((n, p) => n + p.not_in_current, 0).toLocaleString()}</td>
                <td /><td />
              </tr>
            </tfoot>
          </table>
          <details>
            <summary>Where to find each partner · {partners.reduce((n, p) => n + p.places.length, 0)} files and tabs</summary>
            <table className={"rf-sub" + placeCw.className} style={placeCw.style}>
              {placeCw.colgroup}
              <thead><tr>
                <th>File{placeCw.grip("file")}</th><th>Statement{placeCw.grip("statement")}</th>
                <th>Tab{placeCw.grip("sheet")}</th><th className="rf-num">Customers{placeCw.grip("customers")}</th>
              </tr></thead>
              <tbody>
                {partners.map((p) => (
                  <Fragment key={p.partner}>
                    <tr className="rf-line-clean"><td colSpan={4}>{p.partner}</td></tr>
                    {p.places.map((x) => (
                      <tr key={`${x.statement}|${x.file}|${x.sheet}`} className="rf-line-sub">
                        <Ell text={x.file || "(file not known)"} />
                        <td>{x.statement}</td><Ell text={x.sheet} />
                        <td className="rf-num">{x.customers.toLocaleString()}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}

      {/* The main tab strip (AdditionalDetails.tsx): roving tabindex, Left/Right/Home/End. */}
      <div className="tabstrip" role="tablist" aria-label="Customer variance views">
        {TABS.map(([id, label], i) => {
          const n = id === "customers" ? s.customers : id === "explaining" ? result.explaining.total_rows
            : id === "excluded" ? result.excluded.total_rows : undefined;
          return (
            <button key={id} type="button" role="tab" id={`rftab-${id}`} aria-selected={tab === id}
              tabIndex={tab === id ? 0 : -1} className={`tab ${tab === id ? "active" : ""}`}
              onClick={() => setTab(id)}
              onKeyDown={(e) => {
                if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") return;
                e.preventDefault();
                const next = e.key === "Home" ? 0 : e.key === "End" ? TABS.length - 1
                  : e.key === "ArrowRight" ? (i + 1) % TABS.length : (i - 1 + TABS.length) % TABS.length;
                setTab(TABS[next][0]);
                document.getElementById(`rftab-${TABS[next][0]}`)?.focus();
              }}>
              {label}
              {n !== undefined && <span className={`tab-count ${n === 0 ? "tab-count--zero" : ""}`}>{n.toLocaleString()}</span>}
            </button>
          );
        })}
      </div>

      {tab === "customers" && (
        <>
          <div className="rf-toolbar">
            <div className="rf-chips" role="group" aria-label="Filter customers by status">
              {chips.map((k) => (
                <button key={k || "all"} type="button" aria-pressed={filter === k}
                  className={`rf-chip${filter === k ? " active" : ""}${k && k !== REVIEW ? ` rf-chip--${STATUS_TONE[k]}` : ""}`}
                  onClick={() => pickFilter(k)}>
                  {k === REVIEW ? "To review" : k || "All"} <span className="rf-chip-n">{chipCount(k).toLocaleString()}</span>
                </button>
              ))}
              {partner && (
                <button type="button" className="rf-chip active" aria-label={`Clear partner filter ${partner}`}
                  onClick={() => pickPartner(partner)}>
                  Partner: {partner} ×
                </button>
              )}
            </div>
            <div className="rf-toolbar-right">
              <input className="input rf-search" type="search" placeholder="Search A/C No or customer"
                aria-label="Search customers" value={query}
                onChange={(e) => { setQuery(e.target.value); setShown(PAGE); }} />
              <div className="segmented" role="group" aria-label="Layout">
                <button type="button" className={`seg-opt${view === "review" ? " active" : ""}`}
                  aria-pressed={view === "review"} onClick={() => setView("review")}>Review</button>
                <button type="button" className={`seg-opt${view === "grid" ? " active" : ""}`}
                  aria-pressed={view === "grid"} onClick={() => setView("grid")}>Full grid</button>
              </div>
            </div>
          </div>

          {view === "grid" ? (
            <>
              <DataTable frame={gridFrame} pageSize={50} config={customerConfig} keepCuratedColumns
                pinSide="insurer" insurerName={insurerName} title="Customers"
                rowAction={{
                  header: "Details",
                  render: (row) => (
                    <button className="btn btn-tiny" onClick={() => setOpen(String(gridGet(row as Row, "account") ?? ""))}>
                      Details
                    </button>
                  ),
                }} />
              {open && (
                <div className="rf-detail-card">
                  <CustomerDetail account={open} name={String(get(rows.find((r) => get(r, "account") === open) ?? [], "name") ?? "")}
                    reason={String(get(rows.find((r) => get(r, "account") === open) ?? [], "reason") ?? "")}
                    sources={sourcesOf(rows.find((r) => get(r, "account") === open), get)}
                    variance={num(get(rows.find((r) => get(r, "account") === open) ?? [], "variance"))}
                    explaining={explainBy.get(open) ?? []} statement={statementBy.get(open) ?? []}
                    explainFrame={result.explaining} statementFrame={result.transactions}
                    sides={result.side_columns} cut={cut} to={to} onClose={() => setOpen("")} />
                </div>
              )}
            </>
          ) : rows.length === 0 ? (
            <p className="rf-empty">No customers match{filter === REVIEW ? ": every customer agrees between the two statements." : "."}</p>
          ) : (
            <div className="rf-table-wrap">
              <table className={"rf-table" + custCw.className} style={custCw.style}>
                {custCw.colgroup}
                <thead>
                  <tr>
                    <th className="rf-col-x" aria-label="Expand" />
                    {/* Autofit reads every filtered customer, not just those shown so far. */}
                    <th>Customer<small>A/C No</small>{custCw.grip("customer", () => rows.map((r) => String(get(r, "name") ?? "")))}</th>
                    <th className="rf-num">Balance {shortDate(s.previous_cut_off)}<small>previous statement</small>{custCw.grip("prev_close", moneyTexts("prev_close"))}</th>
                    <th className="rf-num">Balance {shortDate(s.previous_cut_off)}<small>current statement</small>{custCw.grip("curr_open", moneyTexts("curr_open"))}</th>
                    <th className="rf-num">Variance{custCw.grip("variance", moneyTexts("variance"))}</th>
                    <th className="rf-num">Movement<small>to {shortDate(s.current_to)}</small>{custCw.grip("movement", moneyTexts("movement"))}</th>
                    <th className="rf-num">Closing {shortDate(s.current_to)}{custCw.grip("curr_close", moneyTexts("curr_close"))}</th>
                    <th>Status{custCw.grip("status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, shown).map((r) => {
                    const acc = String(get(r, "account") ?? "");
                    const st = String(get(r, "status") ?? "");
                    const isOpen = open === acc;
                    return (
                      <Fragment key={acc}>
                        <tr className={`rf-row${isOpen ? " rf-row--open" : ""}`} onClick={() => setOpen(isOpen ? "" : acc)}>
                          <td className="rf-col-x">
                            <button type="button" className="rf-expander" aria-expanded={isOpen}
                              aria-label={`${isOpen ? "Hide" : "Show"} details for ${acc}`}
                              onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? "" : acc); }}>
                              {isOpen ? "▾" : "▸"}
                            </button>
                          </td>
                          <td className="rf-name">
                            <div className="rf-ell" title={String(get(r, "name") ?? "")}>{String(get(r, "name") ?? "") || "—"}</div>
                            <div className="rf-acc">{acc}</div>
                          </td>
                          <Money v={num(get(r, "prev_close"))} />
                          <Money v={num(get(r, "curr_open"))} />
                          <Money v={num(get(r, "variance"))} tone={st !== NO_VARIANCE} />
                          <Money v={num(get(r, "movement"))} />
                          <Money v={num(get(r, "curr_close"))} />
                          <td className="rf-status">
                            <Pill status={st} />
                            {get(r, "cause") ? <span className="rf-cause">{String(get(r, "cause"))}</span> : null}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="rf-detail-row">
                            <td colSpan={8}>
                              <CustomerDetail account={acc} name={String(get(r, "name") ?? "")}
                                reason={String(get(r, "reason") ?? "")} sources={sourcesOf(r, get)}
                                variance={num(get(r, "variance"))}
                                explaining={explainBy.get(acc) ?? []} statement={statementBy.get(acc) ?? []}
                                explainFrame={result.explaining} statementFrame={result.transactions}
                                sides={result.side_columns} cut={cut} to={to} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td>Total · {rows.length.toLocaleString()} {rows.length === 1 ? "customer" : "customers"}</td>
                    {totals.map((t, i) => <Money key={i} v={t} />)}
                    <td />
                  </tr>
                </tfoot>
              </table>
              {rows.length > shown && (
                <button type="button" className="btn rf-more" onClick={() => setShown(shown + PAGE)}>
                  Show {Math.min(PAGE, rows.length - shown)} more of {(rows.length - shown).toLocaleString()}
                </button>
              )}
            </div>
          )}
        </>
      )}
      {tab === "explaining" && (
        <>
          <p className="rf-caption">
            Every row behind a variance at {cut}, one line each. Per customer they add up to that
            customer's variance; the Excel lists each row in its statement's own columns.
          </p>
          <DataTable frame={explainFlat} pageSize={50} config={explainConfig}
            pinSide="insurer" insurerName={insurerName} title="Variance transactions" />
        </>
      )}
      {tab === "excluded" && (
        <>
          <p className="rf-caption">
            Rows dated after each statement's cut-off ({cut} for the previous, {to} for the
            current). None of them enter a balance on this page; they are listed so nothing is dropped.
          </p>
          {(s.not_counted ?? []).length > 0 && (
            <table className={"rf-sub rf-not-counted" + notCountedCw.className} style={notCountedCw.style}>
              {notCountedCw.colgroup}
              <thead><tr>
                {NOT_COUNTED_COLS.map((c) => (
                  <th key={c} className={c === "Rows" || c === "Amount" ? "rf-num" : undefined}>{c}{notCountedCw.grip(c)}</th>
                ))}
              </tr></thead>
              <tbody>
                {s.not_counted.map((n) => (
                  <tr key={`${n.file}|${n.reason}`}>
                    <td>{n.file}</td><td>{n.reason}</td>
                    <td className="rf-num">{n.rows.toLocaleString()}</td><Money v={n.amount} />
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DataTable frame={result.excluded} pageSize={50} config={excludedConfig}
            pinSide="insurer" insurerName={insurerName} title="Rows not counted" />
        </>
      )}
      {tab === "checks" && (
        <div className="rf-checks">
          {(["Previous", "Current"] as const).map((lab) => (
            <FileChecks key={lab} label={lab} f={s.files[lab]}
              cutOff={lab === "Previous" ? cut : to} excluded={s.excluded_rows[lab]}
              balance={lab === "Previous" ? s.totals.prev_close : s.totals.curr_close} />
          ))}
        </div>
      )}
      {rerunning && (
        <>
          <div className="busy-bar" />
          <div className="busy-label" role="status" aria-live="polite">
            <span className="spinner" />
            Applying change — re-running the customer variance check…{rerunElapsed > 0 && ` ${rerunElapsed}s`}
          </div>
        </>
      )}
    </div>
  );
}

// The uploaded file and tab each statement prints the customer in ("" when it is absent).
function sourcesOf(r: Row | undefined, get: (r: Row, c: string) => unknown) {
  return { previous: r ? String(get(r, "previous_source") ?? "") : "",
           current: r ? String(get(r, "current_source") ?? "") : "" };
}

interface DetailProps {
  account: string;
  name: string;
  reason: string;
  sources: { previous: string; current: string };
  variance: number;
  explaining: Row[];
  statement: Row[];
  explainFrame: FrameRecords;
  statementFrame: FrameRecords;
  sides: RollforwardResponse["side_columns"];
  cut: string;
  to: string;
  onClose?: () => void;
}

const MOVED_IN = "Moved from another customer";
const MOVED_OUT = "Moved to another customer";

// One statement's side of a variance row: its cells as printed, or why there are none.
// Same wording as the workbook's Customer Blocks.
function SideCells({ row, ex, side, cols: headers }: { row: Row; ex: (r: Row, c: string) => unknown;
                                                       side: "prev" | "curr"; cols: string[] }) {
  if (!headers.length) return null;
  const amount = ex(row, `${side}_amount`);
  const present = headers.some((c) => ex(row, `${side}:${c}`) !== null && ex(row, `${side}:${c}`) !== undefined);
  if (!present) {
    const change = String(ex(row, "change") ?? "");
    const other = String(ex(row, "counterpart_account") ?? "");
    const text = typeof amount === "number" && !Number.isNaN(amount)
      ? `balance ${fmtAed(amount)} (no transaction row)`
      : side === "prev" && change === MOVED_IN && other ? `was under A/C ${other}`
      : side === "curr" && change === MOVED_OUT && other ? `now under A/C ${other}`
      : `not in the ${side === "prev" ? "previous" : "current"} statement`;
    return <td colSpan={headers.length} className="rf-side-absent">{text}</td>;
  }
  return (
    <>
      {headers.map((c, i) => {
        const v = ex(row, `${side}:${c}`);
        const text = cellText(c, v);
        return typeof v === "number" && MONEY_HEADER.test(c)
          ? <td key={c} className={`rf-num${i === 0 ? " rf-side-start" : ""}`}>{text}</td>
          : <Ell key={c} className={i === 0 ? "rf-side-start" : undefined} text={text} />;
      })}
    </>
  );
}

// One customer: the rows behind its variance, side by side, and its current statement.
function CustomerDetail(p: DetailProps) {
  const [pane, setPane] = useState<"why" | "statement">(p.explaining.length ? "why" : "statement");
  const ex = cols(p.explainFrame);
  const tx = cols(p.statementFrame);
  const last = p.statement.length ? num(tx(p.statement[p.statement.length - 1], "balance")) : 0;
  const pc = p.sides.Previous, cc = p.sides.Current;
  const sidesCw = useColumnWidths(
    useMemo(() => ["change", ...pc.map((c) => `p${c}`), ...cc.map((c) => `c${c}`), "effect", "note"], [pc, cc]),
    p.account);
  const stmtCw = useColumnWidths(STATEMENT_COLS, p.account);
  return (
    <div className="rf-detail">
      <div className="rf-detail-head">
        <div>
          <span className="rf-detail-acc">{p.account}</span> <span className="rf-detail-name">{p.name}</span>
          <p className="rf-detail-source">
            <span>Where to find it</span>
            {" "}Previous: {p.sources.previous || "not in the previous upload"}
            {" · "}Current: {p.sources.current || "not in the current upload"}
          </p>
          {p.reason && <p className="rf-detail-reason">{p.reason}</p>}
        </div>
        <div className="rf-detail-tools">
          <div className="segmented" role="group" aria-label="Detail">
            <button type="button" className={`seg-opt${pane === "why" ? " active" : ""}`} aria-pressed={pane === "why"}
              onClick={() => setPane("why")}>Why it differs ({p.explaining.length})</button>
            <button type="button" className={`seg-opt${pane === "statement" ? " active" : ""}`} aria-pressed={pane === "statement"}
              onClick={() => setPane("statement")}>Statement ({p.statement.length})</button>
          </div>
          {p.onClose && <button type="button" className="rf-close" aria-label="Close details" onClick={p.onClose}>×</button>}
        </div>
      </div>
      {pane === "why" ? (
        p.explaining.length ? (
          <div className="rf-sub-scroll rf-sides-scroll">
            <table className={"rf-sub rf-sides" + sidesCw.className} style={sidesCw.style}>
              {sidesCw.colgroup}
              <thead>
                <tr>
                  <th />
                  {pc.length > 0 && <th colSpan={pc.length} className="rf-side-band rf-side-band--prev">Previous statement</th>}
                  {cc.length > 0 && <th colSpan={cc.length} className="rf-side-band rf-side-band--curr">Current statement</th>}
                  <th colSpan={2} />
                </tr>
                <tr>
                  <th>What changed{sidesCw.grip("change")}</th>
                  {pc.map((c, i) => <th key={`p${c}`} className={`${MONEY_HEADER.test(c) ? "rf-num" : ""}${i === 0 ? " rf-side-start" : ""}`}>{c}{sidesCw.grip(`p${c}`)}</th>)}
                  {cc.map((c, i) => <th key={`c${c}`} className={`${MONEY_HEADER.test(c) ? "rf-num" : ""}${i === 0 ? " rf-side-start" : ""}`}>{c}{sidesCw.grip(`c${c}`)}</th>)}
                  <th className="rf-num rf-side-start">Effect on variance{sidesCw.grip("effect")}</th><th>Note{sidesCw.grip("note")}</th>
                </tr>
              </thead>
              <tbody>
                {p.explaining.map((r, i) => (
                  <tr key={i}>
                    <td><span className="rf-change">{String(ex(r, "change") ?? "")}</span></td>
                    <SideCells row={r} ex={ex} side="prev" cols={pc} />
                    <SideCells row={r} ex={ex} side="curr" cols={cc} />
                    <Money v={num(ex(r, "amount"))} />
                    <Ell className="rf-narr" text={String(ex(r, "note") ?? "")} />
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={1 + pc.length + cc.length}>
                    {p.explaining.length} {p.explaining.length === 1 ? "row adds" : "rows add"} up to the variance at {p.cut}
                  </td>
                  <Money v={p.variance} /><td />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : <p className="rf-empty">No differing rows: both statements agree for this customer up to {p.cut}.</p>
      ) : p.statement.length ? (
        <div className="rf-sub-scroll">
          <table className={"rf-sub" + stmtCw.className} style={stmtCw.style}>
            {stmtCw.colgroup}
            <thead><tr>
              {STATEMENT_COLS.map((c) => (
                <th key={c} className={["Debit", "Credit", "Balance"].includes(c) ? "rf-num" : undefined}>
                  {c === "__flag" ? null : c}{stmtCw.grip(c)}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {p.statement.map((r, i) => {
                const flag = String(tx(r, "variance_flag") ?? "");
                return (
                  <tr key={i} className={flag ? "rf-flagged" : undefined}>
                    <td className="rf-date">{shortDate(tx(r, "date"))}</td>
                    <td>{String(tx(r, "voucher") ?? "")}</td>
                    <Ell className="rf-narr" text={String(tx(r, "narration") ?? "")} />
                    <td className="rf-num">{num(tx(r, "debit")) ? fmtAed(num(tx(r, "debit"))) : ""}</td>
                    <td className="rf-num">{num(tx(r, "credit")) ? fmtAed(num(tx(r, "credit"))) : ""}</td>
                    <Money v={num(tx(r, "balance"))} />
                    <td>{flag && <span className="rf-flag">{flag}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr><td colSpan={5}>Closing at {p.to}</td><Money v={last} /><td /></tr></tfoot>
          </table>
        </div>
      ) : <p className="rf-empty">This customer has no rows in the current statement up to {p.to}.</p>}
    </div>
  );
}

function FileChecks({ label, f, cutOff, excluded, balance }: { label: string; f: RollforwardFileSummary;
                                                            cutOff: string; excluded: number; balance: number }) {
  const dropped = f.reprints?.dropped ?? [];
  const unchained = f.reprints?.unchained_openings ?? [];
  const added = (f.unclosed_blocks ?? []).filter((u) => u.added);
  const held = (f.unclosed_blocks ?? []).filter((u) => !u.added);
  const pasted = f.unread_reports ?? [];
  const breaks = f.balance_breaks ?? { rows: 0, examples: [] };
  const diff = f.tie_out.difference;
  const own = f.own_total_difference;
  return (
    <section className="rf-check-card">
      <h3 className="rf-check-title">{label} statement</h3>
      <table className="rf-sub rf-tie">
        <tbody>
          <tr><td>Balance at {cutOff}</td><Money v={balance} /></tr>
          <tr><td>Add: {f.after_cut_off.rows.toLocaleString()} rows it prints after {cutOff}</td><Money v={f.after_cut_off.amount} /></tr>
          <tr className={Math.abs(own) < 0.01 ? "rf-tie-ok" : "rf-tie-bad"}>
            <td>= Its own customer total {Math.abs(own) < 0.01 ? "✓" : `— differs by ${fmtAed(own)}`}</td>
            <Money v={f.tie_out.customer_total} />
          </tr>
        </tbody>
      </table>
      <table className="rf-sub rf-tie rf-tie--second">
        <tbody>
          <tr><td>Reconciliation closing</td><Money v={f.tie_out.engine_closing} /></tr>
          <tr><td>Sum of customer balances</td><Money v={f.tie_out.customer_total} /></tr>
          <tr><td>Printed customer openings outside that closing</td><Money v={f.tie_out.openings_outside_closing} /></tr>
          <tr className={Math.abs(diff) < 0.01 ? "rf-tie-ok" : "rf-tie-bad"}>
            <td>Difference {Math.abs(diff) < 0.01 ? "✓" : "— check"}</td><Money v={diff} />
          </tr>
        </tbody>
      </table>
      <ul className="rf-check-list">
        <li>{excluded.toLocaleString()} rows dated after {cutOff}: listed, not counted.</li>
        {f.outside_blocks?.rows > 0 && (
          <li>{f.outside_blocks.rows.toLocaleString()} rows printed below a customer's closing row
            (net {fmtAed(f.outside_blocks.net)}): they belong to no customer and count under
            "(below closing row)", in the closing as well.</li>)}
        <li>Reprinted rows counted once: {dropped.reduce((n, d) => n + d.rows, 0).toLocaleString()}
          {dropped.length ? ` across ${dropped.filter((d) => d.account).length} account(s)${dropped.some((d) => !d.account) ? " and the rows below a closing row" : ""}, net ${fmtAed(dropped.reduce((n, d) => n + d.net, 0))}` : ""}.</li>
        {added.length > 0 && (
          <li>{added.length} customer block(s) print no closing row; their rows agree with the running
            balance and close on them: {added.map((u) => `${u.account} ${fmtAed(u.balance)}`).join("; ")}.</li>)}
        {held.length > 0 && (
          <li className="rf-check-warn">{held.length} customer block(s) print no closing row and their rows
            disagree with the running balance, so they are left out of the closing — check:
            {" "}{held.map((u) => `${u.account} rows ${fmtAed(u.balance)} vs printed ${fmtAed(u.printed)}`).join("; ")}.</li>)}
        {unchained.length > 0 && (
          <li className="rf-check-warn">{unchained.length} repeated customer block(s) print an opening that does
            not continue the earlier block; check {unchained.map((u) => u.account).join(", ")}.</li>)}
        {pasted.length > 0 && (
          <li className="rf-check-warn">{pasted.length} report(s) pasted into a tab with different columns
            from its ledger, so neither step reads them:{" "}
            {pasted.map((u) => `${u.account || "no account code"} (${u.items} items, ${u.file}, tab '${u.sheet.trim()}')`).join("; ")}.</li>)}
        {breaks.rows > 0 && (
          <li className="rf-check-warn">{breaks.rows.toLocaleString()} row(s) whose printed running balance does
            not follow from the row above — check:{" "}
            {breaks.examples.map((b) => `${b.account} ${b.voucher} ${b.date} (${fmtAed(b.previous)} → ${fmtAed(b.printed)})`).join("; ")}
            {breaks.rows > breaks.examples.length ? `; and ${breaks.rows - breaks.examples.length} more` : ""}.</li>)}
        <li>Tabs not read: {f.unread_sheets.length
          ? f.unread_sheets.map((u) => `${u.sheet} (${u.reason})`).join("; ") : "none"}.</li>
      </ul>
    </section>
  );
}
